import { useCallback, useRef } from "react";
import type { OrganizationTaskTemplateUpdate } from "@common/contracts/organization-task-template.contract";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUpdateOrganizationTaskTemplateMutation } from "@api/organizationTaskTemplate.api";
import type { OrganizationTaskTemplateCallbacks } from "./organizationTaskTemplatesTableMeta";

type InlineUpdateCallbacks = Pick<
    OrganizationTaskTemplateCallbacks,
    | "onUpdateAssigneeRole"
    | "onUpdateCloseStage"
    | "onUpdateDeadline"
    | "onUpdateDeadlineAnchor"
    | "onUpdateEstimatedTime"
>;

export const useOrganizationTaskTemplateInlineUpdates =
    (): InlineUpdateCallbacks => {
        const { error: errorToast } = useToaster();
        const { mutateAsync } = useUpdateOrganizationTaskTemplateMutation();
        const pendingUpdatesByTemplateIdRef = useRef(
            new Map<string, Promise<void>>(),
        );

        const update = useCallback(
            (templateId: string, body: OrganizationTaskTemplateUpdate) => {
                const pendingUpdates = pendingUpdatesByTemplateIdRef.current;
                const previousUpdate =
                    pendingUpdates.get(templateId) ?? Promise.resolve();
                const nextUpdate = previousUpdate
                    .then(() => mutateAsync({ templateId, body }))
                    .then(() => undefined)
                    .catch(() => {
                        errorToast("Failed to update task template");
                    });
                pendingUpdates.set(templateId, nextUpdate);
            },
            [mutateAsync, errorToast],
        );

        const onUpdateAssigneeRole = useCallback<
            InlineUpdateCallbacks["onUpdateAssigneeRole"]
        >(
            (templateId, assigneeRole) => update(templateId, { assigneeRole }),
            [update],
        );

        const onUpdateCloseStage = useCallback<
            InlineUpdateCallbacks["onUpdateCloseStage"]
        >(
            (templateId, closeStageId) => update(templateId, { closeStageId }),
            [update],
        );

        const onUpdateDeadline = useCallback<
            InlineUpdateCallbacks["onUpdateDeadline"]
        >(
            (templateId, externalDeadline) =>
                update(
                    templateId,
                    externalDeadline === null
                        ? { externalDeadline, deadlineAnchor: null }
                        : { externalDeadline },
                ),
            [update],
        );

        const onUpdateDeadlineAnchor = useCallback<
            InlineUpdateCallbacks["onUpdateDeadlineAnchor"]
        >(
            (templateId, deadlineAnchor) =>
                update(templateId, { deadlineAnchor }),
            [update],
        );

        const onUpdateEstimatedTime = useCallback<
            InlineUpdateCallbacks["onUpdateEstimatedTime"]
        >(
            (templateId, estimatedTime) =>
                update(templateId, { estimatedTime }),
            [update],
        );

        return {
            onUpdateAssigneeRole,
            onUpdateCloseStage,
            onUpdateDeadline,
            onUpdateDeadlineAnchor,
            onUpdateEstimatedTime,
        };
    };
